<template>
    <div class="section pt-2 pb-5">
        <div class="listed-detail mt-1">
            <h3 class="text-center mal-text custom-title">Verification Officers</h3>
        </div>

        <div class="section text-center" v-if="loading">
            <div class="spinner-border text-primary" role="status"></div>
        </div>

        <template v-else-if="voContext.can_suggest">
            <!-- Add form: search an existing member in this district -->
            <div class="card p-3 mb-3" style="border-radius:12px;">
                <div style="font-size:13px;font-weight:700;color:#1a418c;margin-bottom:10px;" class="mal-text">
                    Verification Officer ചേർക്കുക
                </div>

                <!-- Programme selector (only when more than one is enabled) -->
                <template v-if="voContext.vo_programs && voContext.vo_programs.length > 1">
                    <label class="field-label mal-text">പദ്ധതി *</label>
                    <select v-model="program_id" class="form-select form-select-sm mb-2" style="border-radius:10px;">
                        <option value="">— തിരഞ്ഞെടുക്കുക —</option>
                        <option v-for="p in voContext.vo_programs" :key="p.id" :value="p.id">{{ p.title }}</option>
                    </select>
                </template>
                <div v-else-if="voContext.vo_programs && voContext.vo_programs.length === 1" class="mb-2"
                    style="font-size:12px;color:#12a85a;font-weight:700;">
                    <ion-icon name="albums-outline"></ion-icon> {{ voContext.vo_programs[0].title }}
                </div>

                <label class="field-label">Search member (name / mobile) *</label>
                <div style="position:relative;">
                    <input v-model="search" type="text" class="form-control form-control-sm mb-1"
                        :placeholder="selected ? selected.name : 'Search by name or mobile...'"
                        @input="onSearch" @focus="showDrop = true" autocomplete="off" />
                    <div v-if="showDrop && results.length"
                        style="position:absolute;left:0;right:0;top:100%;background:#fff;border:1px solid #e2e8f0;border-radius:10px;box-shadow:0 4px 16px rgba(0,0,0,0.1);max-height:220px;overflow-y:auto;z-index:200;">
                        <div v-for="m in results" :key="m.id" @click="selectMember(m)"
                            style="padding:10px 12px;cursor:pointer;border-bottom:1px solid #f1f5f9;">
                            <div style="font-size:13px;font-weight:700;color:#1e293b;">{{ m.name }}</div>
                            <div style="font-size:11px;color:#64748b;">{{ m.mobile }}</div>
                        </div>
                    </div>
                </div>

                <div v-if="selected"
                    style="background:#eafaf1;border-radius:8px;padding:8px 10px;margin-bottom:8px;display:flex;align-items:center;gap:8px;">
                    <ion-icon name="person-circle-outline" style="color:#12a85a;font-size:20px;"></ion-icon>
                    <div style="flex:1;">
                        <div style="font-size:13px;font-weight:700;color:#12a85a;">{{ selected.name }}</div>
                        <div style="font-size:11px;color:#64748b;">{{ selected.mobile }}</div>
                    </div>
                    <span @click="clearSelected" style="cursor:pointer;color:#94a3b8;font-size:16px;">✕</span>
                </div>

                <!-- Program application summary (total / assigned / balance) for the selected programme -->
                <div v-if="activeProgramSummary" class="mal-text"
                    style="background:#f1f5f9;border-radius:8px;padding:8px 10px;margin-bottom:8px;font-size:12px;color:#1e293b;display:flex;justify-content:space-between;flex-wrap:wrap;gap:4px;">
                    <span>ആകെ അപേക്ഷകൾ: <b>{{ activeProgramSummary.total_applications }}</b></span>
                    <span>അസൈൻ ചെയ്തത്: <b>{{ activeProgramSummary.assigned_total }}</b></span>
                    <span>ബാക്കി: <b>{{ activeProgramSummary.balance }}</b></span>
                </div>

                <label class="field-label mal-text">Quota (എത്ര അപേക്ഷകൾ ഈ ഓഫീസർ പരിശോധിക്കണം?)</label>
                <input v-model="quota" type="number" min="1" class="vo-answer-input mb-2"
                    placeholder="ഉദാ: 10 (ഒഴിവാക്കിയാൽ എല്ലാ അപേക്ഷകളും കാണാം)" />

                <template v-for="q in activeSuggestionQuestions" :key="q.key">
                    <label class="field-label mal-text">{{ q.label }} <span v-if="q.required" class="text-danger">*</span></label>
                    <div v-if="q.type === 'yes_no'" class="vo-yn mb-2">
                        <button type="button" class="vo-yn-opt" :class="{ 'vo-yn-opt--yes': extra[q.key] === 'Yes' }"
                            @click="extra[q.key] = extra[q.key] === 'Yes' ? '' : 'Yes'">Yes</button>
                        <button type="button" class="vo-yn-opt" :class="{ 'vo-yn-opt--no': extra[q.key] === 'No' }"
                            @click="extra[q.key] = extra[q.key] === 'No' ? '' : 'No'">No</button>
                    </div>
                    <textarea v-else-if="q.type === 'textarea'" v-model="extra[q.key]" class="vo-answer-input mb-2" rows="2"></textarea>
                    <select v-else-if="q.type === 'options'" v-model="extra[q.key]" class="vo-answer-input mb-2">
                        <option value="">Select</option>
                        <option v-for="opt in (q.options || [])" :key="opt" :value="opt">{{ opt }}</option>
                    </select>
                    <input v-else v-model="extra[q.key]" type="text" class="vo-answer-input mb-2" />
                </template>

                <p class="field-label mal-text" style="color:#94a3b8;font-weight:500;">
                    ചേർത്ത ശേഷം സ്റ്റേറ്റ് അഡ്മിൻ അപ്രൂവ് ചെയ്താൽ മാത്രമേ ഓഫീസർക്ക് ആക്സസ് ലഭിക്കൂ.
                </p>

                <button type="button" class="btn btn-primary btn-sm w-100" style="border-radius:8px;"
                    :class="{ 'vo-add-blur': !hasEnabledProgram }"
                    :disabled="saving || (hasEnabledProgram && !selected)" @click="onAdd">
                    <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>
                    <ion-icon v-else name="person-add-outline" class="me-1"></ion-icon>
                    Add Verification Officer
                </button>
            </div>

            <!-- District list -->
            <div style="font-size:12px;font-weight:700;color:#64748b;margin-bottom:6px;" class="mal-text">
                ജില്ലയിലെ Verification Officers ({{ list.length }})
            </div>
            <div v-if="!list.length" class="text-center text-muted py-3 mal-text" style="font-size:13px;">
                ഇതുവരെ ആരെയും ചേർത്തിട്ടില്ല
            </div>
            <div v-for="vo in list" :key="vo.id" class="card mb-2" style="border-radius:12px;">
                <div class="card-body" style="padding:12px 14px;">
                    <div class="d-flex justify-content-between align-items-start">
                        <div>
                            <div style="font-size:14px;font-weight:800;color:#1e293b;">{{ vo.name }}</div>
                            <div style="font-size:12px;color:#64748b;">{{ vo.mobile }}</div>
                            <div style="font-size:11px;color:#1a418c;font-weight:600;" v-if="vo.program_title">{{ vo.program_title }}</div>
                            <div style="font-size:11px;color:#94a3b8;" v-if="vo.suggested_by_name">
                                Added by {{ vo.suggested_by_name }}<span v-if="vo.created_at"> · {{ vo.created_at }}</span>
                            </div>
                            <div style="font-size:11px;color:#12a85a;font-weight:700;" class="mal-text" v-if="vo.quota">
                                ക്വാട്ട: {{ vo.quota }} (അസൈൻ ചെയ്തത്: {{ vo.assigned_count }})
                            </div>
                        </div>
                        <div style="display:flex;flex-direction:column;align-items:flex-end;gap:6px;">
                            <span class="badge" :class="statusBadge(vo.approval_status)">
                                {{ statusLabel(vo.approval_status) }}
                            </span>
                            <button v-if="vo.approval_status !== 'approved'" type="button" class="btn btn-sm btn-outline-danger" style="border-radius:8px;padding:2px 8px;font-size:11px;"
                                @click="remove(vo)">Remove</button>
                        </div>
                    </div>

                    <!-- Suggestion Q&A (collapsible, per VO's programme) -->
                    <div v-if="vo.suggestion_questions && vo.suggestion_questions.length" class="mt-2">
                        <a href="javascript:void(0)" @click="qaOpen[vo.id] = !qaOpen[vo.id]"
                            style="font-size:12px;font-weight:700;color:#1a418c;text-decoration:none;display:flex;align-items:center;gap:4px;">
                            ചോദ്യങ്ങൾ
                            <ion-icon :name="qaOpen[vo.id] ? 'chevron-up-outline' : 'chevron-down-outline'"></ion-icon>
                        </a>
                        <div v-show="qaOpen[vo.id]" class="mt-1">
                            <div v-for="q in vo.suggestion_questions" :key="q.key" class="qa-row">
                                <span class="qa-label mal-text">{{ q.label }}</span>
                                <span class="qa-val">{{ (vo.suggested_extra && vo.suggested_extra[q.key]) || '—' }}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </template>

        <div class="listed-detail mt-3" v-else>
            <h4 class="text-center mt-0 mal-text">You have no permission!</h4>
        </div>

        <alert-model :type="alert_type" :message="alert_message" ref="alert_model"></alert-model>
    </div>
</template>

<script>
import UserService from "@/services/user.service";
import AlertModel from "../layouts/AlertModel.vue";

export default {
    name: "SnehaSparshamVoManage",
    components: { AlertModel },
    data() {
        return {
            loading: true,
            saving: false,
            voContext: { can_suggest: false, suggestion_questions: [] },
            list: [],
            suggestionQuestions: [],
            qaOpen: {},
            search: "",
            results: [],
            showDrop: false,
            searchTimer: null,
            selected: null,
            extra: {},
            program_id: "",
            quota: "",
            programSummaries: [],
            alert_type: "",
            alert_message: "",
        };
    },
    computed: {
        hasEnabledProgram() {
            return !!(this.voContext.vo_programs && this.voContext.vo_programs.length);
        },
        selectedProgram() {
            const programs = this.voContext.vo_programs || [];
            if (programs.length === 1) return programs[0];
            return programs.find((p) => String(p.id) === String(this.program_id)) || null;
        },
        // Suggestion questions for the currently-selected programme.
        activeSuggestionQuestions() {
            return (this.selectedProgram && this.selectedProgram.suggestion_questions) || [];
        },
        // Total/assigned/balance summary for the currently-selected programme.
        activeProgramSummary() {
            if (!this.selectedProgram) return null;
            return this.programSummaries.find((s) => String(s.program_id) === String(this.selectedProgram.id)) || null;
        },
        ctx() {
            return {
                user_level: parseInt(this.$route.params.sub_level, 10),
                user_level_id: parseInt(this.$route.params.id, 10),
            };
        },
    },
    mounted() {
        if (!this.$store.state.auth.user) {
            location.replace("/");
            return;
        }
        this.$root.updateParent("Verification Officers");
        this.load();
    },
    methods: {
        load() {
            this.loading = true;
            UserService.authPostRequest("get-sneha-sparsham-vo-context", this.ctx).then(
                (response) => {
                    if (response.data.status === "success") {
                        this.voContext = response.data.data;
                    }
                    if (this.voContext.can_suggest) {
                        this.loadList();
                    } else {
                        this.loading = false;
                    }
                },
                () => { this.loading = false; }
            );
        },
        statusLabel(s) {
            return s === 'approved' ? 'Approved' : s === 'rejected' ? 'Rejected' : 'Pending Approval';
        },
        statusBadge(s) {
            return s === 'approved' ? 'bg-success' : s === 'rejected' ? 'bg-danger' : 'bg-warning text-dark';
        },
        loadList() {
            UserService.authPostRequest("get-sneha-sparsham-vo-list", this.ctx).then(
                (response) => {
                    this.loading = false;
                    if (response.data.status === "success") {
                        this.list = response.data.data.list || [];
                        this.programSummaries = response.data.data.program_summaries || [];
                    }
                },
                () => { this.loading = false; }
            );
        },
        onSearch() {
            this.selected = null;
            clearTimeout(this.searchTimer);
            const q = this.search.trim();
            if (q.length < 2) {
                this.results = [];
                return;
            }
            this.searchTimer = setTimeout(() => {
                UserService.authPostRequest("search-sneha-sparsham-vo-member", {
                    ...this.ctx,
                    query_text: q,
                }).then((res) => {
                    if (res.data.status === "success") {
                        this.results = res.data.data.list || [];
                        this.showDrop = true;
                    }
                }).catch(() => {});
            }, 350);
        },
        selectMember(m) {
            this.selected = m;
            this.search = "";
            this.results = [];
            this.showDrop = false;
        },
        clearSelected() {
            this.selected = null;
        },
        onAdd() {
            // No programme enabled → show error instead of adding.
            if (!this.hasEnabledProgram) {
                this.alert_type = "Warning";
                this.alert_message = "പദ്ധതി ലഭ്യമല്ല.";
                this.$refs.alert_model.openModel();
                return;
            }
            this.save();
        },
        save() {
            if (!this.selected) return;
            const programs = this.voContext.vo_programs || [];
            let programId = this.program_id;
            if (programs.length === 1) programId = programs[0].id;
            if (!programId) {
                this.alert_type = "Warning";
                this.alert_message = "പദ്ധതി തിരഞ്ഞെടുക്കുക.";
                this.$refs.alert_model.openModel();
                return;
            }
            for (const q of this.activeSuggestionQuestions) {
                if (q.required && !(this.extra[q.key] || "").toString().trim()) {
                    this.alert_type = "Warning";
                    this.alert_message = q.label + " is required.";
                    this.$refs.alert_model.openModel();
                    return;
                }
            }
            this.saving = true;
            UserService.authPostRequest("store-sneha-sparsham-vo-suggestion", {
                ...this.ctx,
                member_id: this.selected.id,
                program_id: programId,
                extra_data: this.extra,
                quota: this.quota ? parseInt(this.quota, 10) : null,
            }).then(
                (response) => {
                    this.saving = false;
                    this.alert_type = response.data.status === "success" ? "Success" : "Warning";
                    this.alert_message = response.data.message;
                    this.$refs.alert_model.openModel();
                    if (response.data.status === "success") {
                        this.selected = null;
                        this.extra = {};
                        this.program_id = "";
                        this.quota = "";
                        this.loadList();
                    }
                },
                () => { this.saving = false; }
            );
        },
        remove(vo) {
            if (!confirm("Remove this Verification Officer?")) return;
            UserService.authPostRequest("remove-sneha-sparsham-vo", {
                ...this.ctx,
                id: vo.id,
            }).then((response) => {
                if (response.data.status === "success") this.loadList();
            }).catch(() => {});
        },
    },
};
</script>

<style scoped>
.field-label { font-size: 11px; font-weight: 600; color: #64748b; margin-bottom: 3px; display: block; }
/* Explicit styling (not reliant on Bootstrap form-control/form-select being loaded) so answer
   fields for the portal-defined questions are clearly recognizable as inputs. */
.vo-answer-input {
    display: block;
    width: 100%;
    border: 1.5px solid #cbd5e1;
    border-radius: 10px;
    padding: 8px 10px;
    font-size: 14px;
    color: #1e293b;
    background: #fff;
}
.vo-answer-input:focus { outline: none; border-color: #1a418c; }
.vo-yn { display: flex; gap: 8px; }
.vo-yn-opt {
    flex: 1;
    padding: 8px 0;
    border: 1px solid #dbe1ea;
    border-radius: 8px;
    background: #fff;
    color: #64748b;
    font-size: 13px;
    font-weight: 700;
    cursor: pointer;
}
.vo-yn-opt--yes { background: #eafaf1; border-color: #12a85a; color: #12a85a; }
.vo-yn-opt--no { background: #fff5f5; border-color: #d9534f; color: #d9534f; }
.vo-add-blur { opacity: 0.5; filter: grayscale(0.3); }
.qa-row { display: flex; justify-content: space-between; gap: 10px; padding: 6px 0; border-top: 1px solid #f4f5f7; font-size: 12.5px; }
.qa-label { color: #8a94a6; flex: 0 0 50%; }
.qa-val { color: #1e293b; font-weight: 600; text-align: right; flex: 1; word-break: break-word; }
</style>
